import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const DEFAULT_GATEWAY_BASE =
  "https://creditlibanais-netcommerce.gateway.mastercard.com";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function getGatewayBase(candidate) {
  const raw = String(
    candidate || process.env.REACT_APP_MPGS_GATEWAY_BASE || DEFAULT_GATEWAY_BASE
  ).trim();
  const url = new URL(raw);
  const hostname = url.hostname.toLowerCase();

  if (url.protocol !== "https:" || !hostname.endsWith(".gateway.mastercard.com")) {
    throw new Error("Invalid payment gateway URL.");
  }

  return url.origin;
}

function loadCheckoutSdk(gatewayBase, onError) {
  const src = `${gatewayBase}/static/checkout/checkout.min.js`;
  const existing = document.querySelector(`script[src="${src}"]`);

  if (window.Checkout) return Promise.resolve(window.Checkout);

  return new Promise((resolve, reject) => {
    const callbackName = "hasteMpgsCheckoutError";
    window[callbackName] = (error) => {
      onError(error);
    };

    const script = existing || document.createElement("script");
    script.src = src;
    script.async = true;
    script.setAttribute("data-error", callbackName);
    script.onload = () => {
      if (window.Checkout) resolve(window.Checkout);
      else reject(new Error("Mastercard Checkout SDK did not initialize."));
    };
    script.onerror = () => reject(new Error("Could not load Mastercard Checkout SDK."));

    if (!existing) document.head.appendChild(script);
  });
}

export default function PayStartPage() {
  const query = useQuery();
  const sessionId = String(query.get("sessionId") || "").trim();
  const gatewayCandidate = query.get("gatewayBase") || "";
  const [state, setState] = useState({ stage: "loading", message: "Opening secure payment page..." });

  useEffect(() => {
    let cancelled = false;

    async function startCheckout() {
      if (!/^[A-Za-z0-9_-]{31,35}$/.test(sessionId)) {
        throw new Error("Missing or invalid Mastercard checkout session.");
      }

      const gatewayBase = getGatewayBase(gatewayCandidate);
      const checkout = await loadCheckoutSdk(gatewayBase, (error) => {
        if (cancelled) return;
        const message =
          typeof error === "string"
            ? error
            : error?.explanation || error?.message || "Mastercard rejected the checkout request.";
        setState({ stage: "error", message });
      });

      if (cancelled) return;
      checkout.configure({ session: { id: sessionId } });
      checkout.showPaymentPage();
    }

    startCheckout().catch((error) => {
      if (!cancelled) {
        setState({
          stage: "error",
          message: error?.message || "Unable to open the secure payment page.",
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [gatewayCandidate, sessionId]);

  return (
    <div style={{ minHeight: "70vh", background: "#f3f4f6", padding: "24px 12px" }}>
      <div
        style={{
          maxWidth: 620,
          margin: "28px auto",
          padding: 20,
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 18, color: "#111827" }}>
          Secure card payment
        </div>
        <div style={{ marginTop: 8, color: state.stage === "error" ? "#991b1b" : "#6b7280" }}>
          {state.message}
        </div>
        {state.stage === "error" && (
          <div style={{ marginTop: 14 }}>
            <Link to="/" style={{ color: "#111827", fontWeight: 800 }}>
              Return to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
